import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { approach, learningCycle, productFamilies, progression } from '../data/about';
import { easeOutStrong, fadeUp } from '../utils/motion';
export function AboutPage() {
  return <>
      {/* Hero */}
      <section className="bg-white border-b border-line pt-32 pb-20 lg:pt-44 lg:pb-28" data-id="element-6" data-mp="123ong3skkp2d">
        <motion.div initial={{
        opacity: 0,
        y: 16
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: easeOutStrong
      }} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-7" data-mp="1fvcwtzbfozfn">
          <div className="lg:col-span-5" data-id="element-8" data-mp="1kupvrifkxwyl">
            <p className="text-sm font-medium text-teal mb-5" data-id="element-9" data-mp="1kvr95tk2ivjh">Company</p>
            <h1 className="font-heading font-extrabold text-5xl lg:text-7xl text-ink-dark leading-[1.03] tracking-tight" data-id="element-10" data-mp="3rkw4t9ay2ji5">
              About SyNeural
            </h1>
          </div>
          <div className="lg:col-span-7 lg:pt-10" data-id="element-11" data-mp="3lqs2dmq1iz4x">
            <p className="text-2xl text-ink-dark leading-relaxed mb-6" data-id="element-12" data-mp="3ls329bgd7mqr">
              SyNeural builds systems that help people develop sound judgment,
              researchers connect ideas, and institutions make decisions they
              can explain and improve.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-13" data-mp="3du7hu83uwhm">
              Our work brings together two product families: the{' '}
              <a href="/human-capability" className="text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy" data-id="element-14" data-mp="8dzqkm0b7ao3">
                Human Capability Platform
              </a>
              , which includes the Civic Judgment Index (CJI) and Neurocognitive
              Civic Judgment Index (NCJI), and the{' '}
              <a href="/semantic-knowledge" className="text-navy font-semibold underline decoration-line underline-offset-4 hover:decoration-navy" data-id="element-15" data-mp="2j4gigq4kei3x">
                Semantic Knowledge Platform (SKP)
              </a>
              , which connects ideas, research, and institutional knowledge.
            </p>
            <p className="text-lg text-ink-dark leading-relaxed border-l-2 border-teal pl-5" data-id="element-16" data-mp="3mfm1y3vk314j">
              We believe intelligence grows when people can think clearly,
              knowledge can move across disciplines, and institutions can learn
              from what they do.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Mission */}
      <section className="bg-surface-bg py-20 lg:py-28" data-id="element-17" data-mp="98keczndagdh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18" data-mp="97srcd7u5qyt">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-19" data-mp="mzyyj9t0gnm4">
            <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-20" data-mp="ixvligqp1ayq">
              Our mission
            </h2>
            <div className="lg:col-span-7" data-id="element-21" data-mp="wvk4fj17a815">
              <p className="font-heading font-bold text-2xl lg:text-3xl text-ink-dark leading-snug mb-6" data-id="element-22" data-mp="rzv2xa5meh27">
                SyNeural’s mission is to strengthen human capability and make
                knowledge more useful in the world.
              </p>
              <p className="text-lg text-ink-muted leading-relaxed" data-id="element-23" data-mp="1ghc8z5bfmrfi">
                We build tools that help researchers discover connections, help
                people develop judgment, and help institutions understand
                complex problems and act responsibly. Our focus is on
                progression: turning knowledge into insight, insight into
                development, and development into better decisions.
              </p>
            </div>
          </motion.div>

          <motion.ol {...fadeUp} aria-label="Progression" className="mt-16 grid grid-cols-2 md:grid-cols-4 border-t border-line" data-id="element-24" data-mp="iu3hmtor2o5l">
            {progression.map((stage, index) => <li key={stage} className="flex items-center justify-between gap-4 py-6 md:pr-6 border-b md:border-b-0 border-line" data-id="element-25" data-mp="dxd2q9oolylr" data-mpi={index}>
                <span className={`font-heading font-bold text-xl ${index === progression.length - 1 ? 'text-navy' : 'text-ink-dark'}`} data-id="element-26" data-mp="1uk6pads8042">
                  {stage}
                </span>
                {index < progression.length - 1 && <ArrowRight size={20} aria-hidden="true" className="hidden md:block text-teal shrink-0" data-id="element-27" data-mp="3o9v7brags0o5" />}
              </li>)}
          </motion.ol>
        </div>
      </section>

      {/* What we build */}
      <section className="bg-white py-20 lg:py-28" data-id="element-28" data-mp="3aa3v9w3icucs">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-29" data-mp="3aavqh5t70q2j">
          <motion.h2 {...fadeUp} className="font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight mb-14" data-id="element-30" data-mp="35c2e5gpn753y">
            What we build
          </motion.h2>
          <div className="border-b border-line" data-id="element-31" data-mp="13kz16ksa5eg8">
            {productFamilies.map(product => <motion.article key={product.name} {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-16 border-t border-line py-12" data-id="element-32" data-mp="1bzvccja6ykrl">
                <div className="lg:col-span-4" data-id="element-33" data-mp="1kujiscri68hz">
                  <p className="text-sm font-medium text-teal mb-2" data-id="element-34" data-mp="1psbfrl708kt1">{product.includes}</p>
                  <h3 className="font-heading font-bold text-2xl lg:text-3xl text-ink-dark mb-5" data-id="element-35" data-mp="1pu5wenb8tqrb">
                    {product.name}
                  </h3>
                  <a href={product.link} className="inline-flex items-center gap-2 text-navy font-semibold hover:text-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-36" data-mp="x4fd7zur7yoc">
                    Explore the platform
                    <ArrowRight size={16} data-id="element-37" data-mp="2dip4b1qds1iv" />
                  </a>
                </div>
                <div className="lg:col-span-8 space-y-5" data-id="element-38" data-mp="2igh2gdhmcjvi">
                  {product.paragraphs.map((paragraph, index) => <p key={paragraph} className={`leading-relaxed ${index === 0 ? 'text-xl text-ink-dark' : 'text-lg text-ink-muted'}`} data-id="element-39" data-mp="1srjrq69sp7rz" data-mpi={index}>
                      {paragraph}
                    </p>)}
                </div>
              </motion.article>)}
          </div>
        </div>
      </section>

      {/* Why these platforms belong together */}
      <section className="bg-navy-deep text-white py-20 lg:py-28" data-id="element-40" data-mp="1a00opzfrttpy">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-41" data-mp="154ts1bkx5paj">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-42" data-mp="1jseud39yzkhe">
            <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl tracking-tight leading-tight" data-id="element-43" data-mp="1f0wwio38u00n">
              Why these platforms belong together
            </h2>
            <div className="lg:col-span-7" data-id="element-44" data-mp="md0xvdu45y18">
              <p className="font-heading font-bold text-2xl lg:text-3xl leading-snug mb-6" data-id="element-45" data-mp="mbzjp0hswvfa">
                Knowledge systems help us understand the world. Human capability
                systems help us use that understanding well.
              </p>
              <p className="text-lg text-white/75 leading-relaxed" data-id="element-46" data-mp="2o287q4h429y3">
                SKP gives SyNeural a structured way to connect and refine ideas.
                The Human Capability Platform applies that knowledge to
                learning, judgment, opportunity, and leadership development.
                Together, they support a continuous cycle:
              </p>
            </div>
          </motion.div>

          <motion.ol {...fadeUp} aria-label="Continuous cycle" className="mt-14 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/15 border border-white/15 rounded-lg overflow-hidden" data-id="element-47" data-mp="37qh2mma1gu1a">
            {learningCycle.map((stage, index) => <li key={stage} className="bg-navy-deep p-6 lg:p-8" data-id="element-48" data-mp="20cvsonz3r7nv" data-mpi={index}>
                <span className="block text-sm text-teal font-medium mb-3" data-id="element-49" data-mp="20a17uvc6fktx">
                  {index === learningCycle.length - 1 ? 'Then repeat' : `Stage ${index + 1}`}
                </span>
                <span className="font-heading font-bold text-xl" data-id="element-50" data-mp="2n36zwkf0vsro">{stage}</span>
              </li>)}
          </motion.ol>

          <motion.p {...fadeUp} className="mt-12 max-w-4xl text-lg text-white leading-relaxed border-l-2 border-green-soft pl-6" data-id="element-51" data-mp="2e0pbq0ft4q21">
            When a finding could affect someone’s opportunities or
            responsibilities, its basis must remain visible and open to human
            review, correction, appeal, and reassessment.
          </motion.p>
        </div>
      </section>

      {/* Vision */}
      <section className="bg-white py-20 lg:py-28" data-id="element-52" data-mp="2wyiff4y9940h">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-53" data-mp="3ue5uhwwttvcb">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-54" data-mp="3plmfcvihqs5e">
            Our vision
          </h2>
          <div className="lg:col-span-7" data-id="element-55" data-mp="5rrawuulgw0l">
            <p className="font-heading font-bold text-2xl lg:text-3xl text-ink-dark leading-snug mb-6" data-id="element-56" data-mp="5un64bf256lc">
              SyNeural’s long-term vision is a global infrastructure for human
              capability and connected knowledge.
            </p>
            <p className="text-lg text-ink-muted leading-relaxed mb-5" data-id="element-57" data-mp="jt3o4yidejis">
              We see a future where researchers can work across disconnected
              fields, individuals have clearer pathways to develop their
              strengths, and institutions can understand the reasoning behind
              their decisions and improve over time.
            </p>
            <p className="text-lg text-ink-dark leading-relaxed" data-id="element-58" data-mp="12iti5jeb719n">
              The goal is to build intelligence throughout the systems people
              rely on—and expand what people are capable of doing together.
            </p>
          </div>
        </motion.div>
      </section>

      {/* Leadership */}
      <section className="bg-surface-bg py-20 lg:py-28 border-y border-line" data-id="element-59" data-mp="2f5j8yp1y380u">
        <motion.div {...fadeUp} className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16" data-id="element-60" data-mp="2f3g8tc72vyie">
          <h2 className="lg:col-span-5 font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight" data-id="element-61" data-mp="2owdutsba45lr">
            Leadership
          </h2>
          <div className="lg:col-span-7 flex flex-col sm:flex-row gap-8" data-id="element-62" data-mp="2xw0n9om1612o">
            <img src="https://cdn.magicpatterns.com/uploads/aGzifJuZ6XFUAFRAawshw8/IMG_1407.jpg" alt="Portrait of Shawn Blake, CEO of SyNeural" width={160} height={200} loading="lazy" className="w-32 h-40 sm:w-40 sm:h-48 shrink-0 rounded-lg object-cover object-[50%_30%] border border-line bg-surface-muted" data-id="element-63" data-mp="2xydcmmu4bdpr" />
            <div data-id="element-64" data-mp="1dia3yuzzmxkx">
              <h3 className="font-heading font-bold text-2xl text-ink-dark" data-id="element-65" data-mp="3qqgpgx3yuj67">Shawn Blake</h3>
              <p className="text-teal font-medium mb-5" data-id="element-66" data-mp="3m007czp7a9di">Chief Executive Officer</p>
              <p className="text-lg text-ink-muted leading-relaxed" data-id="element-67" data-mp="3ly5ors1eitcl">
                Shawn Blake leads SyNeural’s work across cognitive systems,
                semantic knowledge, and institutional design. His focus is
                bringing these disciplines together to help researchers connect
                ideas, individuals develop their capabilities, and
                organizations turn complex knowledge into responsible action.
              </p>
            </div>
          </div>
        </motion.div>
      </section>

      {/* Approach */}
      <section className="bg-white py-20 lg:py-28" data-id="element-68" data-mp="3voqm7c5c9rtx">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-69" data-mp="2p9px9qeqe9wu">
          <motion.h2 {...fadeUp} className="font-heading font-bold text-4xl lg:text-5xl text-ink-dark tracking-tight mb-14" data-id="element-70" data-mp="2pbaph9dkpgrj">
            The SyNeural approach
          </motion.h2>
          <motion.dl {...fadeUp} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-10 border-t border-line" data-id="element-71" data-mp="2lppfowhpfz0p">
            {approach.map(item => <div key={item.title} className="py-8 border-b border-line lg:border-b-0" data-id="element-72" data-mp="2ct82lnps5r2b">
                <dt className="font-heading font-bold text-xl text-ink-dark mb-3" data-id="element-73" data-mp="2hodq2qytzdsi">{item.title}</dt>
                <dd className="text-ink-muted leading-relaxed" data-id="element-74" data-mp="22zzoejd5hvdj">{item.body}</dd>
              </div>)}
          </motion.dl>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy text-white py-20 lg:py-28" data-id="element-75" data-mp="1u1xcv9vsjkc0">
        <motion.div {...fadeUp} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-76" data-mp="1y1pra37v8kpa">
          <h2 className="font-heading font-bold text-4xl lg:text-5xl tracking-tight mb-6" data-id="element-77" data-mp="1k2pzimp23pow">
            Build with SyNeural
          </h2>
          <p className="text-xl text-white/80 leading-relaxed mb-10" data-id="element-78" data-mp="1p085c47s1oso">
            Whether you are connecting scientific research, developing human
            capability, or designing a more intelligent institution, SyNeural is
            building the systems to support that work.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center" data-id="element-79" data-mp="1feoy5z39wwva">
            <a href="/contact" className="inline-flex items-center justify-center gap-2 bg-white text-navy hover:bg-surface-muted font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-80" data-mp="1aekrt1w2xiis">
              Request a Demo
              <ArrowRight size={18} data-id="element-81" data-mp="1y4ybo54igb2a" />
            </a>
            <a href="/partnerships" className="inline-flex items-center justify-center border border-white/40 text-white hover:bg-white/10 font-semibold px-6 py-3.5 rounded-md transition-colors duration-150 whitespace-nowrap" data-id="element-82" data-mp="1y3n5xzri8t8j">
              Start a Partnership Conversation
            </a>
          </div>
        </motion.div>
      </section>
    </>;
}